import { useState } from 'react';

interface SymbolDef {
  display: string;
  insert: string;
  title: string;
}

interface SymbolGroup {
  id: string;
  label: string;
  icon: string;
  symbols: SymbolDef[];
}

const SYMBOL_GROUPS: SymbolGroup[] = [
  {
    id: 'functions',
    label: 'Functions',
    icon: 'ri-function-line',
    symbols: [
      { display: 'sin', insert: 'sin(', title: 'Sine function' },
      { display: 'cos', insert: 'cos(', title: 'Cosine function' },
      { display: 'tan', insert: 'tan(', title: 'Tangent function' },
      { display: 'asin', insert: 'asin(', title: 'Arcsine' },
      { display: 'acos', insert: 'acos(', title: 'Arccosine' },
      { display: 'atan', insert: 'atan(', title: 'Arctangent' },
      { display: 'ln', insert: 'ln(', title: 'Natural logarithm' },
      { display: 'log', insert: 'log(', title: 'Base-10 logarithm' },
      { display: 'exp', insert: 'exp(', title: 'Exponential e^x' },
      { display: '√', insert: 'sqrt(', title: 'Square root' },
      { display: '∛', insert: 'cbrt(', title: 'Cube root' },
      { display: 'abs', insert: 'abs(', title: 'Absolute value' },
      { display: '⌊ ⌋', insert: 'floor(', title: 'Floor function' },
      { display: '⌈ ⌉', insert: 'ceil(', title: 'Ceiling function' },
      { display: 'mod', insert: ' mod ', title: 'Modulo' },
      { display: '∫', insert: 'integrate ', title: 'Integral' },
      { display: 'd/dx', insert: 'derivative of ', title: 'Derivative' },
      { display: 'lim', insert: 'limit as x->0 of ', title: 'Limit' },
      { display: 'Σ', insert: 'sum(', title: 'Summation' },
      { display: 'solve', insert: 'solve ', title: 'Solve equation' },
    ],
  },
  {
    id: 'operators',
    label: 'Operators',
    icon: 'ri-calculator-2-line',
    symbols: [
      { display: '^', insert: '^', title: 'Power / exponent' },
      { display: '²', insert: '^2', title: 'Squared' },
      { display: '³', insert: '^3', title: 'Cubed' },
      { display: 'ⁿ', insert: '^n', title: 'To the n' },
      { display: '×', insert: '*', title: 'Multiply' },
      { display: '÷', insert: '/', title: 'Divide' },
      { display: '±', insert: '+-', title: 'Plus or minus' },
      { display: '≤', insert: '<=', title: 'Less than or equal' },
      { display: '≥', insert: '>=', title: 'Greater than or equal' },
      { display: '≠', insert: '!=', title: 'Not equal' },
      { display: '≈', insert: '≈', title: 'Approximately equal' },
      { display: '()', insert: '()', title: 'Parentheses' },
      { display: '[ ]', insert: '[', title: 'Bracket' },
      { display: '|x|', insert: 'abs(', title: 'Absolute value bars' },
      { display: '/', insert: '/', title: 'Fraction bar' },
    ],
  },
  {
    id: 'greek',
    label: 'Greek',
    icon: 'ri-text',
    symbols: [
      { display: 'α', insert: 'alpha', title: 'Alpha (variable)' },
      { display: 'β', insert: 'beta', title: 'Beta (variable)' },
      { display: 'γ', insert: 'gamma', title: 'Gamma (variable)' },
      { display: 'δ', insert: 'delta', title: 'Delta (variable)' },
      { display: 'ε', insert: 'epsilon', title: 'Epsilon (variable)' },
      { display: 'θ', insert: 'theta', title: 'Theta (variable)' },
      { display: 'λ', insert: 'lambda', title: 'Lambda (variable)' },
      { display: 'μ', insert: 'mu', title: 'Mu (variable)' },
      { display: 'ν', insert: 'nu', title: 'Nu (variable)' },
      { display: 'ξ', insert: 'xi', title: 'Xi (variable)' },
      { display: 'π', insert: 'pi', title: 'Pi constant' },
      { display: 'ρ', insert: 'rho', title: 'Rho (variable)' },
      { display: 'σ', insert: 'sigma', title: 'Sigma (variable)' },
      { display: 'τ', insert: 'tau', title: 'Tau (variable)' },
      { display: 'φ', insert: 'phi', title: 'Phi / golden ratio' },
      { display: 'ψ', insert: 'psi', title: 'Psi (variable)' },
      { display: 'ω', insert: 'omega', title: 'Omega (variable)' },
      { display: 'Γ', insert: 'GAMMA', title: 'Capital Gamma' },
      { display: 'Δ', insert: 'DELTA', title: 'Capital Delta' },
      { display: 'Σ', insert: 'SIGMA', title: 'Capital Sigma (sum)' },
      { display: 'Π', insert: 'PI', title: 'Capital Pi (product)' },
      { display: 'Ω', insert: 'OMEGA', title: 'Capital Omega' },
    ],
  },
  {
    id: 'constants',
    label: 'Constants',
    icon: 'ri-numbers-line',
    symbols: [
      { display: 'π', insert: 'pi', title: 'Pi ≈ 3.14159…' },
      { display: 'e', insert: 'e', title: 'Euler\'s number ≈ 2.71828…' },
      { display: '∞', insert: 'Infinity', title: 'Infinity' },
      { display: '-∞', insert: '-Infinity', title: 'Negative infinity' },
      { display: 'i', insert: 'i', title: 'Imaginary unit √(-1)' },
      { display: 'φ', insert: 'phi', title: 'Golden ratio ≈ 1.618…' },
      { display: 'c', insert: 'speedOfLight', title: 'Speed of light' },
      { display: 'g', insert: '9.81', title: 'Gravitational acceleration' },
      { display: 'k_B', insert: '1.380649e-23', title: 'Boltzmann constant' },
      { display: 'N_A', insert: '6.02214076e23', title: 'Avogadro\'s number' },
      { display: '√2', insert: 'sqrt(2)', title: '√2 ≈ 1.41421…' },
      { display: '√3', insert: 'sqrt(3)', title: '√3 ≈ 1.73205…' },
    ],
  },
];

/* Quick-access row — most used symbols */
const QUICK_SYMBOLS: SymbolDef[] = [
  { display: '^', insert: '^', title: 'Power' },
  { display: '²', insert: '^2', title: 'Squared' },
  { display: '√', insert: 'sqrt(', title: 'Square root' },
  { display: 'π', insert: 'pi', title: 'Pi' },
  { display: '∫', insert: 'integrate ', title: 'Integral' },
  { display: 'd/dx', insert: 'derivative of ', title: 'Derivative' },
  { display: '∞', insert: 'Infinity', title: 'Infinity' },
  { display: 'e', insert: 'e', title: "Euler's number" },
  { display: 'sin', insert: 'sin(', title: 'Sine' },
  { display: 'cos', insert: 'cos(', title: 'Cosine' },
  { display: 'ln', insert: 'ln(', title: 'Natural log' },
  { display: 'abs', insert: 'abs(', title: 'Absolute value' },
];

interface Props {
  onInsert: (text: string) => void;
}

export default function SymbolPicker({ onInsert }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [activeGroup, setActiveGroup] = useState('functions');

  const group = SYMBOL_GROUPS.find(g => g.id === activeGroup) ?? SYMBOL_GROUPS[0];

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mt-3">
      {/* Quick-access bar + toggle */}
      <div className="flex items-center gap-1 px-3 py-2 bg-slate-50 border-b border-slate-100">
        {/* Quick symbols */}
        <div className="flex items-center gap-0.5 flex-1 flex-wrap">
          {QUICK_SYMBOLS.map(sym => (
            <button
              key={sym.insert}
              onClick={() => onInsert(sym.insert)}
              title={sym.title}
              className="h-7 px-2 rounded-md bg-white border border-slate-200 hover:border-violet-400 hover:bg-violet-50 hover:text-violet-700 text-slate-700 font-mono text-xs transition-all cursor-pointer whitespace-nowrap"
            >
              {sym.display}
            </button>
          ))}
        </div>
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(v => !v)}
          title="Show all symbols"
          className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ml-1 ${
            expanded
              ? 'bg-violet-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-violet-50 hover:text-violet-600'
          }`}
        >
          <i className="ri-apps-2-line text-xs"></i>
          {expanded ? 'Less' : 'More'}
        </button>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div>
          {/* Group tabs */}
          <div className="flex border-b border-slate-100 bg-white">
            {SYMBOL_GROUPS.map(g => (
              <button
                key={g.id}
                onClick={() => setActiveGroup(g.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-all cursor-pointer border-b-2 ${
                  activeGroup === g.id
                    ? 'border-violet-500 text-violet-700 bg-violet-50/40'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <i className={`${g.icon} text-xs`}></i>
                <span className="hidden sm:inline">{g.label}</span>
              </button>
            ))}
          </div>

          {/* Symbol grid */}
          <div className="p-3 grid grid-cols-5 gap-1.5 sm:grid-cols-8 md:grid-cols-10 bg-white max-h-40 overflow-y-auto">
            {group.symbols.map(sym => (
              <button
                key={`${sym.insert}-${sym.display}`}
                onClick={() => onInsert(sym.insert)}
                title={sym.title}
                className="h-9 px-1.5 rounded-lg bg-slate-50 border border-slate-200 hover:border-violet-400 hover:bg-violet-50 hover:text-violet-700 text-slate-700 font-mono text-xs transition-all cursor-pointer flex items-center justify-center"
              >
                {sym.display}
              </button>
            ))}
          </div>

          {/* Hint row */}
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 font-medium">
              <i className="ri-information-line mr-1"></i>
              Click a symbol to insert it at cursor position
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
